import React from 'react';
import { Button } from './ui/button';
import { AlertTriangle, RefreshCw, Home } from 'lucide-react';
import { API } from '../config/api';

/**
 * Error Boundary component to catch and handle React errors gracefully
 * Logs errors to server for monitoring and prevents red error overlay in production
 */
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { 
      hasError: false, 
      error: null, 
      errorInfo: null,
      errorId: null 
    };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    console.error('[ErrorBoundary] Caught error:', error, errorInfo);
    this.setState({ errorInfo });
    
    // Log error to server
    this.logErrorToServer(error, errorInfo);
  }

  async logErrorToServer(error, errorInfo) {
    try {
      const token = localStorage.getItem('token');
      const headers = {
        'Content-Type': 'application/json',
        ...(token && { 'Authorization': `Bearer ${token}` })
      };

      const response = await fetch(`${API}/client-error`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          message: error?.message || String(error),
          stack: error?.stack,
          route: window.location.pathname,
          userAgent: navigator.userAgent,
          buildVersion: process.env.REACT_APP_VERSION || 'unknown',
          componentStack: errorInfo?.componentStack,
          errorInfo: {
            type: 'react_error_boundary',
            url: window.location.href,
            timestamp: new Date().toISOString()
          }
        })
      });

      if (response.ok) {
        const data = await response.json();
        this.setState({ errorId: data.error_id });
      }
    } catch (e) {
      console.error('Failed to log error to server:', e);
    }
  }

  handleReset = () => {
    this.setState({ hasError: false, error: null, errorInfo: null, errorId: null });
  };

  handleReload = () => {
    window.location.reload();
  };

  handleGoHome = () => {
    window.location.href = '/';
  };

  render() {
    if (this.state.hasError) {
      const isDev = process.env.NODE_ENV !== 'production';
      
      return (
        <div className="min-h-screen flex items-center justify-center px-4 py-12 bg-background">
          <div className="max-w-md w-full text-center">
            <div className="mb-6">
              <div className="w-16 h-16 mx-auto rounded-full bg-red-500/20 flex items-center justify-center mb-4">
                <AlertTriangle className="w-8 h-8 text-red-500" />
              </div>
              <h1 className="font-heading text-2xl font-bold mb-2">Something went wrong</h1>
              <p className="text-muted-foreground">
                We're sorry, but something unexpected happened. Please try again.
              </p>
              {this.state.errorId && (
                <p className="text-xs text-muted-foreground mt-2">
                  Error ID: {this.state.errorId}
                </p>
              )}
            </div>
            
            {/* Show error details in development only */}
            {isDev && this.state.error && (
              <div className="mb-6 p-4 rounded-lg bg-muted/30 border border-border text-left">
                <p className="text-sm font-mono text-red-400 break-all">
                  {this.state.error.toString()}
                </p>
                {this.state.errorInfo && (
                  <pre className="mt-2 text-xs text-muted-foreground overflow-auto max-h-40">
                    {this.state.errorInfo.componentStack}
                  </pre>
                )}
              </div>
            )}
            
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Button onClick={this.handleReset} variant="outline">
                <RefreshCw className="w-4 h-4 mr-2" />
                Try Again
              </Button>
              <Button onClick={this.handleReload} variant="outline">
                Reload Page
              </Button>
              <Button onClick={this.handleGoHome}>
                <Home className="w-4 h-4 mr-2" />
                Go Home
              </Button>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
